import React from "react";
import lockTypes from "../../data/lockTypes.json";
import sills from "../../data/sills.json";
import hingeColours from "../../data/hingeColours.json";
import InsertDesigns from "../../data/insertDesigns.json";
import DoorStyles from "../../data/doorStyles.json";

// ── Style tokens ────────────────────────────────────────────────────────────
const lbl = {
    display: "block",
    marginBottom: "0.3rem",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#64748b",
};
const inputBase = {
    width: "100%",
    padding: "0.5rem 0.65rem",
    fontSize: "0.93rem",
    color: "#1e293b",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
};
const focusHandlers = {
    onFocus: (e) => {
        e.target.style.borderColor = "#2c7aa0";
        e.target.style.boxShadow = "0 0 0 3px rgba(44,122,160,0.12)";
        e.target.style.background = "#ffffff";
    },
    onBlur: (e) => {
        e.target.style.borderColor = "#e2e8f0";
        e.target.style.boxShadow = "none";
        e.target.style.background = "#f8fafc";
    },
};
const fw = { marginBottom: "0.65rem" };
const g2 = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.65rem",
    marginBottom: "0.65rem",
};
const g3 = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "0.65rem",
    marginBottom: "0.65rem",
};
const subHeader = {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#94a3b8",
    margin: "0.75rem 0 0.4rem",
    display: "block",
};
const copyBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    padding: "0.3rem 0.75rem",
    marginBottom: "0.75rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#2c7aa0",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "6px",
    cursor: "pointer",
};

// ── Accordion section ────────────────────────────────────────────────────────
const DoorSection = ({ title, defaultOpen = false, onCopy, copyLabel, children }) => {
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <div
            style={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                marginBottom: "0.5rem",
                overflow: "hidden",
            }}
        >
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.55rem 0.9rem",
                    background: open ? "#2c7aa0" : "#f1f5f9",
                    color: open ? "#fff" : "#334155",
                    fontWeight: 600,
                    fontSize: "0.88rem",
                    border: "none",
                    cursor: "pointer",
                    transition: "background 0.15s ease",
                }}
            >
                <span>{title}</span>
                <i
                    className={`fas fa-chevron-${open ? "up" : "down"}`}
                    style={{ fontSize: "0.72rem" }}
                />
            </button>
            {open && (
                <div style={{ padding: "0.85rem 1rem", background: "#fff" }}>
                    {onCopy && (
                        <button type="button" onClick={onCopy} style={copyBtnStyle}>
                            <i className="fas fa-copy" /> {copyLabel}
                        </button>
                    )}
                    {children}
                </div>
            )}
        </div>
    );
};

// ── Field wrapper ────────────────────────────────────────────────────────────
const F = ({ label, children, err }) => (
    <div style={fw}>
        <label style={lbl}>{label}</label>
        {children}
        {err && (
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.75rem", color: "#dc2626" }}>{err}</p>
        )}
    </div>
);

// ── Component ────────────────────────────────────────────────────────────────
const FormSaleDoor = (props) => {
    const { newSale, setNewSale, renderDoorLeft, renderValidations, validationErrors } = props;

    const handleUpdate = (set, field, value, name) => {
        if (field === "po" || field === "cost") {
            set((state) => ({
                ...state,
                [field]: value,
            }));
        } else if (
            field === "brickToBrick" ||
            field === "sill" ||
            field === "sillExtension" ||
            field === "doorSystem"
        ) {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    [field]: value,
                },
            }));
        } else if (name === "jamb") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    jamb: {
                        ...state.productSpecification.jamb,
                        [field]: value,
                    },
                },
            }));
        } else if (field === "frameSizeHeight") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    frameSize: {
                        ...state.productSpecification.frameSize,
                        height: value,
                    },
                },
            }));
        } else if (field === "frameSizeWidth") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    frameSize: {
                        ...state.productSpecification.frameSize,
                        width: value,
                    },
                },
            }));
        } else if (name === "doorLeftDoor") {
            if (
                newSale.productSpecification.sill !== "" &&
                newSale.productSpecification.frameSize.height !== ""
            ) {
                if (newSale.productSpecification.sill === "Self Draining Black") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 3.25;
                } else if (newSale.productSpecification.sill === "Self Draining Mill") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 3.25;
                } else if (newSale.productSpecification.sill === "Low Profile Mill") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 2.5;
                } else if (newSale.productSpecification.sill === "Adjustable Mill") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 2.75;
                } else if (newSale.productSpecification.sill === "Adjustable Black") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 2.75;
                } else if (newSale.productSpecification.sill === "Outswing") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 2.5;
                } else if (newSale.productSpecification.sill === "Handicap") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 1.875;
                } else if (newSale.productSpecification.sill === "Flat Cap Mill") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 3;
                } else if (newSale.productSpecification.sill === "Flat Cap Black") {
                    newSale.productSpecification.doorLeft.doorSlabHeight =
                        newSale.productSpecification.frameSize.height - 3;
                }
            }

            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorLeft: {
                        ...state.productSpecification.doorLeft,
                        [field]: value,
                    },
                },
            }));
        } else if (name === "doorLeft") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorLeft: {
                        ...state.productSpecification.doorLeft,
                        [field]: value,
                    },
                },
            }));
        } else if (name === "doorLeftInsert") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorLeft: {
                        ...state.productSpecification.doorLeft,
                        doorInsert: {
                            ...state.productSpecification.doorLeft.doorInsert,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "doorLeftLock") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorLeft: {
                        ...state.productSpecification.doorLeft,
                        lock: {
                            ...state.productSpecification.doorLeft.lock,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "doorLeftHinge") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorLeft: {
                        ...state.productSpecification.doorLeft,
                        hinge: {
                            ...state.productSpecification.doorLeft.hinge,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "doorMiddle") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorMiddle: {
                        ...state.productSpecification.doorMiddle,
                        [field]: value,
                    },
                },
            }));
        } else if (name === "doorMiddleInsert") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorMiddle: {
                        ...state.productSpecification.doorMiddle,
                        doorInsert: {
                            ...state.productSpecification.doorMiddle.doorInsert,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "doorMiddleLock") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorMiddle: {
                        ...state.productSpecification.doorMiddle,
                        lock: {
                            ...state.productSpecification.doorMiddle.lock,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "doorMiddleHinge") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorMiddle: {
                        ...state.productSpecification.doorMiddle,
                        hinge: {
                            ...state.productSpecification.doorMiddle.hinge,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "doorRight") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorRight: {
                        ...state.productSpecification.doorRight,
                        [field]: value,
                    },
                },
            }));
        } else if (name === "doorRightInsert") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorRight: {
                        ...state.productSpecification.doorRight,
                        doorInsert: {
                            ...state.productSpecification.doorRight.doorInsert,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "doorRightLock") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorRight: {
                        ...state.productSpecification.doorRight,
                        lock: {
                            ...state.productSpecification.doorRight.lock,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "doorRightHinge") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    doorRight: {
                        ...state.productSpecification.doorRight,
                        hinge: {
                            ...state.productSpecification.doorRight.hinge,
                            [field]: value,
                        },
                    },
                },
            }));
        } else if (name === "sidelightLeft") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    sidelightLeft: {
                        ...state.productSpecification.sidelightLeft,
                        [field]: value,
                    },
                },
            }));
        } else if (name === "sidelightRight") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    sidelightRight: {
                        ...state.productSpecification.sidelightRight,
                        [field]: value,
                    },
                },
            }));
        } else if (name === "transom") {
            set((state) => ({
                ...state,
                productSpecification: {
                    ...state.productSpecification,
                    transom: {
                        ...state.productSpecification.transom,
                        [field]: value,
                    },
                },
            }));
        }
    };

    const copyDoorFields = (event) => {
        event.preventDefault();

        if (event.target.id === "doorMiddlebutton") {
            setNewSale({
                ...newSale,
                productSpecification: {
                    ...newSale.productSpecification,
                    doorMiddle: newSale.productSpecification.doorLeft,
                },
            });
        } else if (event.target.id === "doorRightbutton") {
            setNewSale({
                ...newSale,
                productSpecification: {
                    ...newSale.productSpecification,
                    doorRight: newSale.productSpecification.doorLeft,
                },
            });
        } else if (event.target.id === "sidelightbutton") {
            setNewSale({
                ...newSale,
                productSpecification: {
                    ...newSale.productSpecification,
                    sidelightRight: newSale.productSpecification.sidelightLeft,
                },
            });
        }
    };

    // ── Door fields helper (reused per door panel) ─────────────────────────────
    const renderDoorFields = (doorKey, lockName, hingeName, insertName, doorName) => {
        const door = newSale.productSpecification[doorKey];
        return (
            <>
                {/* Material / Brand / Style / Paint */}
                <span style={subHeader}>Door</span>
                <div style={g2}>
                    <F label="Material">
                        <select
                            style={inputBase}
                            {...focusHandlers}
                            name="doorMaterial"
                            value={door.doorMaterial}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "doorMaterial", e.target.value, doorName)
                            }
                        >
                            <option value="">Select</option>
                            <option value="Steel">Steel</option>
                            <option value="Fibreglass">Fibreglass</option>
                            <option value="Other">Other</option>
                        </select>
                    </F>
                    <F label="Brand">
                        <select
                            style={inputBase}
                            {...focusHandlers}
                            name="doorBrand"
                            value={door.doorBrand}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "doorBrand", e.target.value, doorName)
                            }
                        >
                            {door.doorMaterial === "" && (
                                <option value="">Please Select Door Material First</option>
                            )}
                            {door.doorMaterial !== "" && <option value="">Select</option>}
                            {door.doorMaterial === "Steel" && <option value="Dusco">Dusco</option>}
                            {door.doorMaterial === "Steel" && (
                                <option value="Novatech">Novatech</option>
                            )}
                            {door.doorMaterial === "Steel" && (
                                <option value="Trutech">Trutech</option>
                            )}
                            {door.doorMaterial === "Fibreglass" && (
                                <option value="Mennie">Mennie</option>
                            )}
                            {door.doorMaterial === "Fibreglass" && (
                                <option value="Richersons">Richersons</option>
                            )}
                            {door.doorMaterial !== "" && <option value="Other">Other</option>}
                        </select>
                    </F>
                </div>
                <div style={g2}>
                    <F label="Door Style">
                        <select
                            style={inputBase}
                            {...focusHandlers}
                            name="doorStyle"
                            value={door.doorStyle}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "doorStyle", e.target.value, doorName)
                            }
                        >
                            {door.doorBrand === "" && (
                                <option value="">Please Select Door Brand First</option>
                            )}
                            {door.doorBrand !== "" && <option value="">Select</option>}
                            {DoorStyles.map((ds) =>
                                door.doorBrand === ds.brand ? (
                                    <option key={ds.style} value={ds.style}>
                                        {ds.style}
                                    </option>
                                ) : null,
                            )}
                        </select>
                    </F>
                    <F label="Paint Out">
                        <input
                            style={inputBase}
                            {...focusHandlers}
                            type="text"
                            name="paintOut"
                            value={door.paintOut}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "paintOut", e.target.value, doorName)
                            }
                            placeholder="Factory White"
                        />
                    </F>
                </div>
                <div style={g2}>
                    <F label="Paint In">
                        <input
                            style={inputBase}
                            {...focusHandlers}
                            type="text"
                            name="paintIn"
                            value={door.paintIn}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "paintIn", e.target.value, doorName)
                            }
                            placeholder="Factory White"
                        />
                    </F>
                </div>

                {/* Door Slab Size */}
                <span style={subHeader}>Door Slab Size</span>
                <div style={g2}>
                    <F label="Door Width">
                        <input
                            style={inputBase}
                            {...focusHandlers}
                            type="number"
                            name="doorSlabWidth"
                            value={door.doorSlabWidth}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "doorSlabWidth", e.target.value, doorName)
                            }
                        />
                    </F>
                    <F label="Door Height">
                        <input
                            style={inputBase}
                            {...focusHandlers}
                            type="number"
                            name="doorSlabHeight"
                            value={door.doorSlabHeight}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "doorSlabHeight", e.target.value, doorName)
                            }
                        />
                    </F>
                </div>

                {/* Door Insert */}
                <F
                    label="Door Insert"
                    err={
                        validationErrors.length > 0 && validationErrors.includes("doorInsert")
                            ? "Required"
                            : null
                    }
                >
                    <select
                        style={inputBase}
                        {...focusHandlers}
                        name="doorInsert"
                        value={door.doorInsert.doorInsert}
                        onChange={(e) =>
                            handleUpdate(setNewSale, "doorInsert", e.target.value, insertName)
                        }
                    >
                        <option value="">Select</option>
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                    </select>
                </F>

                {door.doorInsert.doorInsert === "Yes" && (
                    <>
                        <span style={subHeader}>Door Insert</span>
                        <div style={fw}>
                            <F label="Insert Design">
                                <input
                                    style={inputBase}
                                    {...focusHandlers}
                                    type="text"
                                    list="insertDesigns"
                                    name="design"
                                    value={door.doorInsert.design}
                                    onChange={(e) =>
                                        handleUpdate(
                                            setNewSale,
                                            "design",
                                            e.target.value,
                                            insertName,
                                        )
                                    }
                                />
                                <datalist id="insertDesigns">
                                    {InsertDesigns.map((id) => (
                                        <option key={id.design} value={id.design} />
                                    ))}
                                </datalist>
                            </F>
                        </div>
                        <div style={g3}>
                            <F label="Width">
                                <input
                                    style={inputBase}
                                    {...focusHandlers}
                                    type="number"
                                    name="width"
                                    value={door.doorInsert.width}
                                    onChange={(e) =>
                                        handleUpdate(
                                            setNewSale,
                                            "width",
                                            e.target.value,
                                            insertName,
                                        )
                                    }
                                />
                            </F>
                            <F label="Height">
                                <input
                                    style={inputBase}
                                    {...focusHandlers}
                                    type="number"
                                    name="height"
                                    value={door.doorInsert.height}
                                    onChange={(e) =>
                                        handleUpdate(
                                            setNewSale,
                                            "height",
                                            e.target.value,
                                            insertName,
                                        )
                                    }
                                />
                            </F>
                        </div>
                        <div style={g2}>
                            <F label="Quantity">
                                <input
                                    style={inputBase}
                                    {...focusHandlers}
                                    type="number"
                                    name="quantity"
                                    value={door.doorInsert.quantity}
                                    onChange={(e) =>
                                        handleUpdate(
                                            setNewSale,
                                            "quantity",
                                            e.target.value,
                                            insertName,
                                        )
                                    }
                                />
                            </F>
                            <F label="Position">
                                <input
                                    style={inputBase}
                                    {...focusHandlers}
                                    type="text"
                                    name="position"
                                    value={door.doorInsert.position}
                                    onChange={(e) =>
                                        handleUpdate(
                                            setNewSale,
                                            "position",
                                            e.target.value,
                                            insertName,
                                        )
                                    }
                                    placeholder="Center"
                                />
                            </F>
                        </div>
                    </>
                )}

                {/* Lock */}
                <span style={subHeader}>Lock</span>
                <div style={g3}>
                    <F label="Lock Type">
                        <select
                            style={inputBase}
                            {...focusHandlers}
                            name="lockType"
                            value={door.lock.lockType}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "lockType", e.target.value, lockName)
                            }
                        >
                            {lockTypes.map((lt, i) => (
                                <option key={i} value={lt}>
                                    {lt}
                                </option>
                            ))}
                        </select>
                    </F>
                    <F label="Distance From Top">
                        <input
                            style={inputBase}
                            {...focusHandlers}
                            name="distanceFromTop"
                            value={door.lock.distanceFromTop}
                            onChange={(e) =>
                                handleUpdate(
                                    setNewSale,
                                    "distanceFromTop",
                                    e.target.value,
                                    lockName,
                                )
                            }
                        />
                    </F>
                    <F label="Lock Style">
                        <input
                            style={inputBase}
                            {...focusHandlers}
                            type="text"
                            name="lockStyle"
                            value={door.lock.lockStyle}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "lockStyle", e.target.value, lockName)
                            }
                        />
                    </F>
                </div>

                {/* Hinge */}
                <span style={subHeader}>Hinge</span>
                <div style={g3}>
                    <F label="Hinge Colour">
                        <select
                            style={inputBase}
                            {...focusHandlers}
                            name="hingeColour"
                            value={door.hinge.hingeColour}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "hingeColour", e.target.value, hingeName)
                            }
                        >
                            <option value="">Select</option>
                            {hingeColours.map((hc, i) => (
                                <option key={i} value={hc}>
                                    {hc}
                                </option>
                            ))}
                        </select>
                    </F>
                    <F label="Self Closing">
                        <select
                            style={inputBase}
                            {...focusHandlers}
                            name="selfClosing"
                            value={door.hinge.selfClosing}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "selfClosing", e.target.value, hingeName)
                            }
                        >
                            <option value="">Select</option>
                            <option value="No">No</option>
                            <option value="Yes">Yes</option>
                        </select>
                    </F>
                    <F label="Hinge Position">
                        <select
                            style={inputBase}
                            {...focusHandlers}
                            name="position"
                            value={door.hinge.position}
                            onChange={(e) =>
                                handleUpdate(setNewSale, "position", e.target.value, hingeName)
                            }
                        >
                            <option value="">Select</option>
                            <option value="Left">Left</option>
                            <option value="Right">Right</option>
                        </select>
                    </F>
                </div>

                {/* Door Open */}
                <F label="Door Open">
                    <select
                        style={inputBase}
                        {...focusHandlers}
                        name="open"
                        value={door.open}
                        onChange={(e) => handleUpdate(setNewSale, "open", e.target.value, doorKey)}
                    >
                        <option value="">Select</option>
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                    </select>
                </F>
            </>
        );
    };

    const copyToMiddle = () =>
        copyDoorFields({ preventDefault: () => {}, target: { id: "doorMiddlebutton" } });
    const copyToRight = () =>
        copyDoorFields({ preventDefault: () => {}, target: { id: "doorRightbutton" } });
    const copyToRight2 = () =>
        copyDoorFields({ preventDefault: () => {}, target: { id: "sidelightbutton" } });

    const isDouble =
        newSale.productSpecification.doorSystem === "Double Door" ||
        newSale.productSpecification.doorSystem === "Triple Door";
    const isTriple = newSale.productSpecification.doorSystem === "Triple Door";

    return (
        <div>
            {/* Door System selector */}
            <div style={fw}>
                <F label="Door System">
                    <select
                        style={inputBase}
                        {...focusHandlers}
                        name="doorSystem"
                        value={newSale.productSpecification.doorSystem}
                        onChange={(e) => handleUpdate(setNewSale, "doorSystem", e.target.value)}
                    >
                        <option value="">Select</option>
                        <option value="Single Door">Single Door</option>
                        <option value="Double Door">Double Door</option>
                        <option value="Triple Door">Triple Door</option>
                    </select>
                </F>
            </div>

            {newSale.productSpecification.doorSystem !== "" && (
                <div>
                    <h3 style={{ fontWeight: 900, marginBottom: "0.75rem" }}>
                        Door Specifications
                    </h3>

                    {/* Brick to Brick */}
                    <div style={fw}>
                        <F label="Brick To Brick">
                            <input
                                style={inputBase}
                                {...focusHandlers}
                                type="text"
                                name="brickToBrick"
                                value={newSale.productSpecification.brickToBrick}
                                onChange={(e) =>
                                    handleUpdate(setNewSale, "brickToBrick", e.target.value)
                                }
                            />
                        </F>
                    </div>

                    {/* Frame Size */}
                    <span style={subHeader}>Frame Size</span>
                    <div style={g2}>
                        <F label="Width">
                            <input
                                style={inputBase}
                                {...focusHandlers}
                                type="number"
                                name="frameSizeWidth"
                                value={newSale.productSpecification.frameSize.width}
                                onChange={(e) =>
                                    handleUpdate(setNewSale, "frameSizeWidth", e.target.value)
                                }
                            />
                        </F>
                        <F label="Height">
                            <input
                                style={inputBase}
                                {...focusHandlers}
                                type="number"
                                name="frameSizeHeight"
                                value={newSale.productSpecification.frameSize.height}
                                onChange={(e) =>
                                    handleUpdate(setNewSale, "frameSizeHeight", e.target.value)
                                }
                            />
                        </F>
                    </div>

                    {/* Sill */}
                    <span style={subHeader}>Sill</span>
                    <div style={g2}>
                        <F label="Type">
                            <select
                                style={inputBase}
                                {...focusHandlers}
                                name="sill"
                                value={newSale.productSpecification.sill}
                                onChange={(e) => handleUpdate(setNewSale, "sill", e.target.value)}
                            >
                                <option value="">Select</option>
                                {sills.map((s, i) => (
                                    <option key={i} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </F>
                        <F label="Extension Size">
                            <select
                                style={inputBase}
                                {...focusHandlers}
                                name="sillExtension"
                                value={newSale.productSpecification.sillExtension}
                                onChange={(e) =>
                                    handleUpdate(setNewSale, "sillExtension", e.target.value)
                                }
                            >
                                <option value="">Select</option>
                                <option value="1.5">1.5</option>
                                <option value="2.5">2.5</option>
                                <option value="3.5">3.5</option>
                                <option value="4.5">4.5</option>
                            </select>
                        </F>
                    </div>

                    {/* Jamb */}
                    <span style={subHeader}>Jamb</span>
                    <div style={g2}>
                        <F label="Size">
                            <input
                                style={inputBase}
                                {...focusHandlers}
                                type="text"
                                list="jambList"
                                name="jambSize"
                                value={newSale.productSpecification.jamb.jambSize}
                                onChange={(e) =>
                                    handleUpdate(setNewSale, "jambSize", e.target.value, "jamb")
                                }
                            />
                            <datalist id="jambList">
                                <option value="6.625">6.625</option>
                                <option value="4.625">4.625</option>
                            </datalist>
                        </F>
                        <F label="Material">
                            <select
                                style={inputBase}
                                {...focusHandlers}
                                name="jambMaterial"
                                value={newSale.productSpecification.jamb.jambMaterial}
                                onChange={(e) =>
                                    handleUpdate(setNewSale, "jambMaterial", e.target.value, "jamb")
                                }
                            >
                                <option value="Composite Smooth">Composite Smooth</option>
                                <option value="Composite Woodgrain">Composite Woodgrain</option>
                                <option value="Vinyl Wrap">Vinyl Wrap</option>
                                <option value="See Notes">See Notes</option>
                            </select>
                        </F>
                    </div>

                    {/* ── Doors ── */}
                    <span style={subHeader}>Doors</span>
                    <DoorSection title="Door Left" defaultOpen={true}>
                        {renderDoorFields(
                            "doorLeft",
                            "doorLeftLock",
                            "doorLeftHinge",
                            "doorLeftInsert",
                            "doorLeftDoor",
                        )}
                    </DoorSection>

                    <DoorSection
                        title="Middle Door"
                        defaultOpen={isDouble}
                        onCopy={copyToMiddle}
                        copyLabel="Copy Door Left"
                    >
                        {renderDoorFields(
                            "doorMiddle",
                            "doorMiddleLock",
                            "doorMiddleHinge",
                            "doorMiddleInsert",
                            "doorMiddle",
                        )}
                    </DoorSection>

                    <DoorSection
                        title="Right Door"
                        defaultOpen={isTriple}
                        onCopy={copyToRight}
                        copyLabel="Copy Door Left"
                    >
                        {renderDoorFields(
                            "doorRight",
                            "doorRightLock",
                            "doorRightHinge",
                            "doorRightInsert",
                            "doorRight",
                        )}
                    </DoorSection>

                    {/* ── Side Lights ── */}
                    <span style={{ ...subHeader, marginTop: "1.1rem" }}>Side Lights</span>

                    {/* Left Sidelight */}
                    <DoorSection title="Left Sidelight" defaultOpen={true}>
                        <F label="Side Light">
                            <select
                                style={inputBase}
                                {...focusHandlers}
                                name="sidelight"
                                value={newSale.productSpecification.sidelightLeft.sidelight}
                                onChange={(e) =>
                                    handleUpdate(
                                        setNewSale,
                                        "sidelight",
                                        e.target.value,
                                        "sidelightLeft",
                                    )
                                }
                            >
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                            </select>
                        </F>

                        {newSale.productSpecification.sidelightLeft.sidelight === "Yes" && (
                            <>
                                <F label="Sidelight Panels">
                                    <select
                                        style={inputBase}
                                        {...focusHandlers}
                                        name="panel"
                                        value={newSale.productSpecification.sidelightLeft.panel}
                                        onChange={(e) =>
                                            handleUpdate(
                                                setNewSale,
                                                "panel",
                                                e.target.value,
                                                "sidelightLeft",
                                            )
                                        }
                                    >
                                        <option value="">Select</option>
                                        <option value="0">0</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                    </select>
                                </F>

                                {newSale.productSpecification.sidelightLeft.panel > 0 && (
                                    <>
                                        <span style={subHeader}>Panel 1 Size</span>
                                        <div style={g2}>
                                            <F label="Width">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="panelWidth"
                                                    value={
                                                        newSale.productSpecification.sidelightLeft
                                                            .panelWidth
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "panelWidth",
                                                            e.target.value,
                                                            "sidelightLeft",
                                                        )
                                                    }
                                                />
                                            </F>
                                            <F label="Height">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="panelHeight"
                                                    value={
                                                        newSale.productSpecification.sidelightLeft
                                                            .panelHeight
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "panelHeight",
                                                            e.target.value,
                                                            "sidelightLeft",
                                                        )
                                                    }
                                                />
                                            </F>
                                        </div>
                                    </>
                                )}

                                {newSale.productSpecification.sidelightLeft.panel > 1 && (
                                    <>
                                        <span style={subHeader}>Panel 2 Size</span>
                                        <div style={g2}>
                                            <F label="Width">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="panelWidth2"
                                                    value={
                                                        newSale.productSpecification.sidelightLeft
                                                            .panelWidth2
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "panelWidth2",
                                                            e.target.value,
                                                            "sidelightLeft",
                                                        )
                                                    }
                                                />
                                            </F>
                                            <F label="Height">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="panelHeight2"
                                                    value={
                                                        newSale.productSpecification.sidelightLeft
                                                            .panelHeight2
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "panelHeight2",
                                                            e.target.value,
                                                            "sidelightLeft",
                                                        )
                                                    }
                                                />
                                            </F>
                                        </div>
                                    </>
                                )}

                                <F label="Glass Insert">
                                    <select
                                        style={inputBase}
                                        {...focusHandlers}
                                        name="insert"
                                        value={newSale.productSpecification.sidelightLeft.insert}
                                        onChange={(e) =>
                                            handleUpdate(
                                                setNewSale,
                                                "insert",
                                                e.target.value,
                                                "sidelightLeft",
                                            )
                                        }
                                    >
                                        <option value="">Select</option>
                                        <option value="No">No</option>
                                        <option value="Yes">Yes</option>
                                    </select>
                                </F>

                                {newSale.productSpecification.sidelightLeft.insert === "Yes" && (
                                    <>
                                        <F label="Insert Design">
                                            <input
                                                style={inputBase}
                                                {...focusHandlers}
                                                type="text"
                                                list="insertDesigns"
                                                name="insertDesign"
                                                value={
                                                    newSale.productSpecification.sidelightLeft
                                                        .insertDesign
                                                }
                                                onChange={(e) =>
                                                    handleUpdate(
                                                        setNewSale,
                                                        "insertDesign",
                                                        e.target.value,
                                                        "sidelightLeft",
                                                    )
                                                }
                                            />
                                            <datalist id="insertDesigns">
                                                {InsertDesigns.map((id) => (
                                                    <option key={id.design} value={id.design} />
                                                ))}
                                            </datalist>
                                        </F>
                                        <span style={subHeader}>Size</span>
                                        <div style={g2}>
                                            <F label="Width">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="insertWidth"
                                                    value={
                                                        newSale.productSpecification.sidelightLeft
                                                            .insertWidth
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "insertWidth",
                                                            e.target.value,
                                                            "sidelightLeft",
                                                        )
                                                    }
                                                />
                                            </F>
                                            <F label="Height">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="insertHeight"
                                                    value={
                                                        newSale.productSpecification.sidelightLeft
                                                            .insertHeight
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "insertHeight",
                                                            e.target.value,
                                                            "sidelightLeft",
                                                        )
                                                    }
                                                />
                                            </F>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </DoorSection>

                    {/* Right Sidelight */}
                    <DoorSection
                        title="Right Sidelight"
                        defaultOpen={true}
                        onCopy={copyToRight2}
                        copyLabel="Copy Left Sidelight"
                    >
                        <F label="Side Light">
                            <select
                                style={inputBase}
                                {...focusHandlers}
                                name="sidelight"
                                value={newSale.productSpecification.sidelightRight.sidelight}
                                onChange={(e) =>
                                    handleUpdate(
                                        setNewSale,
                                        "sidelight",
                                        e.target.value,
                                        "sidelightRight",
                                    )
                                }
                            >
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                            </select>
                        </F>

                        {newSale.productSpecification.sidelightRight.sidelight === "Yes" && (
                            <>
                                <F label="Sidelight Panels">
                                    <select
                                        style={inputBase}
                                        {...focusHandlers}
                                        name="panel"
                                        value={newSale.productSpecification.sidelightRight.panel}
                                        onChange={(e) =>
                                            handleUpdate(
                                                setNewSale,
                                                "panel",
                                                e.target.value,
                                                "sidelightRight",
                                            )
                                        }
                                    >
                                        <option value="">Select</option>
                                        <option value="0">0</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                    </select>
                                </F>

                                {newSale.productSpecification.sidelightRight.panel > 0 && (
                                    <>
                                        <span style={subHeader}>Panel 1 Size</span>
                                        <div style={g2}>
                                            <F label="Width">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="panelWidth"
                                                    value={
                                                        newSale.productSpecification.sidelightRight
                                                            .panelWidth
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "panelWidth",
                                                            e.target.value,
                                                            "sidelightRight",
                                                        )
                                                    }
                                                />
                                            </F>
                                            <F label="Height">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="panelHeight"
                                                    value={
                                                        newSale.productSpecification.sidelightRight
                                                            .panelHeight
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "panelHeight",
                                                            e.target.value,
                                                            "sidelightRight",
                                                        )
                                                    }
                                                />
                                            </F>
                                        </div>
                                    </>
                                )}

                                {newSale.productSpecification.sidelightRight.panel > 1 && (
                                    <>
                                        <span style={subHeader}>Panel 2 Size</span>
                                        <div style={g2}>
                                            <F label="Width">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="panelWidth2"
                                                    value={
                                                        newSale.productSpecification.sidelightRight
                                                            .panelWidth2
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "panelWidth2",
                                                            e.target.value,
                                                            "sidelightRight",
                                                        )
                                                    }
                                                />
                                            </F>
                                            <F label="Height">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="panelHeight2"
                                                    value={
                                                        newSale.productSpecification.sidelightRight
                                                            .panelHeight2
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "panelHeight2",
                                                            e.target.value,
                                                            "sidelightRight",
                                                        )
                                                    }
                                                />
                                            </F>
                                        </div>
                                    </>
                                )}

                                <F label="Glass Insert">
                                    <select
                                        style={inputBase}
                                        {...focusHandlers}
                                        name="insert"
                                        value={newSale.productSpecification.sidelightRight.insert}
                                        onChange={(e) =>
                                            handleUpdate(
                                                setNewSale,
                                                "insert",
                                                e.target.value,
                                                "sidelightRight",
                                            )
                                        }
                                    >
                                        <option value="">Select</option>
                                        <option value="No">No</option>
                                        <option value="Yes">Yes</option>
                                    </select>
                                </F>

                                {newSale.productSpecification.sidelightRight.insert === "Yes" && (
                                    <>
                                        <F label="Insert Design">
                                            <input
                                                style={inputBase}
                                                {...focusHandlers}
                                                type="text"
                                                list="insertDesigns"
                                                name="insertDesign"
                                                value={
                                                    newSale.productSpecification.sidelightRight
                                                        .insertDesign
                                                }
                                                onChange={(e) =>
                                                    handleUpdate(
                                                        setNewSale,
                                                        "insertDesign",
                                                        e.target.value,
                                                        "sidelightRight",
                                                    )
                                                }
                                            />
                                            <datalist id="insertDesigns">
                                                {InsertDesigns.map((id) => (
                                                    <option key={id.design} value={id.design} />
                                                ))}
                                            </datalist>
                                        </F>
                                        <span style={subHeader}>Size</span>
                                        <div style={g2}>
                                            <F label="Width">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="insertWidth"
                                                    value={
                                                        newSale.productSpecification.sidelightRight
                                                            .insertWidth
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "insertWidth",
                                                            e.target.value,
                                                            "sidelightRight",
                                                        )
                                                    }
                                                />
                                            </F>
                                            <F label="Height">
                                                <input
                                                    style={inputBase}
                                                    {...focusHandlers}
                                                    type="number"
                                                    name="insertHeight"
                                                    value={
                                                        newSale.productSpecification.sidelightRight
                                                            .insertHeight
                                                    }
                                                    onChange={(e) =>
                                                        handleUpdate(
                                                            setNewSale,
                                                            "insertHeight",
                                                            e.target.value,
                                                            "sidelightRight",
                                                        )
                                                    }
                                                />
                                            </F>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </DoorSection>

                    {/* ── Transom ── */}
                    <span style={{ ...subHeader, marginTop: "1.1rem" }}>Transom</span>
                    <DoorSection title="Transom" defaultOpen={true}>
                        <F label="Transom">
                            <select
                                style={inputBase}
                                {...focusHandlers}
                                name="transomBox"
                                value={newSale.productSpecification.transom.transomBox}
                                onChange={(e) =>
                                    handleUpdate(
                                        setNewSale,
                                        "transomBox",
                                        e.target.value,
                                        "transom",
                                    )
                                }
                            >
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                            </select>
                        </F>

                        {newSale.productSpecification.transom.transomBox === "Yes" && (
                            <>
                                <F label="Transom Type">
                                    <select
                                        style={inputBase}
                                        {...focusHandlers}
                                        name="transomType"
                                        value={newSale.productSpecification.transom.transomType}
                                        onChange={(e) =>
                                            handleUpdate(
                                                setNewSale,
                                                "transomType",
                                                e.target.value,
                                                "transom",
                                            )
                                        }
                                    >
                                        <option value="">Select</option>
                                        <option value="Regular">Regular</option>
                                        <option value="Arc">Arc</option>
                                    </select>
                                </F>

                                {newSale.productSpecification.transom.transomType === "Arc" && (
                                    <div style={g2}>
                                        <F label="Arc Height">
                                            <input
                                                style={inputBase}
                                                {...focusHandlers}
                                                type="number"
                                                name="transomArcHeight"
                                                value={
                                                    newSale.productSpecification.transom
                                                        .transomArcHeight
                                                }
                                                onChange={(e) =>
                                                    handleUpdate(
                                                        setNewSale,
                                                        "transomArcHeight",
                                                        e.target.value,
                                                        "transom",
                                                    )
                                                }
                                            />
                                        </F>
                                        <F label="Arc Legs">
                                            <input
                                                style={inputBase}
                                                {...focusHandlers}
                                                type="number"
                                                name="transomArcLegs"
                                                value={
                                                    newSale.productSpecification.transom
                                                        .transomArcLegs
                                                }
                                                onChange={(e) =>
                                                    handleUpdate(
                                                        setNewSale,
                                                        "transomArcLegs",
                                                        e.target.value,
                                                        "transom",
                                                    )
                                                }
                                            />
                                        </F>
                                    </div>
                                )}

                                <span style={subHeader}># Of Transom Box</span>
                                <div style={g2}>
                                    <F label="Row">
                                        <input
                                            style={inputBase}
                                            {...focusHandlers}
                                            type="number"
                                            name="transomRow"
                                            value={newSale.productSpecification.transom.transomRow}
                                            onChange={(e) =>
                                                handleUpdate(
                                                    setNewSale,
                                                    "transomRow",
                                                    e.target.value,
                                                    "transom",
                                                )
                                            }
                                            placeholder="1"
                                        />
                                    </F>
                                    <F label="Column">
                                        <input
                                            style={inputBase}
                                            {...focusHandlers}
                                            type="number"
                                            name="transomColumn"
                                            value={
                                                newSale.productSpecification.transom.transomColumn
                                            }
                                            onChange={(e) =>
                                                handleUpdate(
                                                    setNewSale,
                                                    "transomColumn",
                                                    e.target.value,
                                                    "transom",
                                                )
                                            }
                                            placeholder="1"
                                        />
                                    </F>
                                </div>

                                <span style={subHeader}>Insert</span>
                                <div style={g3}>
                                    <F label="Width">
                                        <input
                                            style={inputBase}
                                            {...focusHandlers}
                                            type="number"
                                            name="transomInsertWidth"
                                            value={
                                                newSale.productSpecification.transom
                                                    .transomInsertWidth
                                            }
                                            onChange={(e) =>
                                                handleUpdate(
                                                    setNewSale,
                                                    "transomInsertWidth",
                                                    e.target.value,
                                                    "transom",
                                                )
                                            }
                                        />
                                    </F>
                                    <F label="Height">
                                        <input
                                            style={inputBase}
                                            {...focusHandlers}
                                            type="number"
                                            name="transomInsertHeight"
                                            value={
                                                newSale.productSpecification.transom
                                                    .transomInsertHeight
                                            }
                                            onChange={(e) =>
                                                handleUpdate(
                                                    setNewSale,
                                                    "transomInsertHeight",
                                                    e.target.value,
                                                    "transom",
                                                )
                                            }
                                        />
                                    </F>
                                    <F label="Design">
                                        <input
                                            style={inputBase}
                                            {...focusHandlers}
                                            type="text"
                                            list="insertDesigns"
                                            name="transomInsertDesign"
                                            value={
                                                newSale.productSpecification.transom
                                                    .transomInsertDesign
                                            }
                                            onChange={(e) =>
                                                handleUpdate(
                                                    setNewSale,
                                                    "transomInsertDesign",
                                                    e.target.value,
                                                    "transom",
                                                )
                                            }
                                        />
                                        <datalist id="insertDesigns">
                                            {InsertDesigns.map((id) => (
                                                <option key={id.design} value={id.design} />
                                            ))}
                                        </datalist>
                                    </F>
                                </div>
                            </>
                        )}
                    </DoorSection>

                    {/* Render validations */}
                    {renderValidations.length > 0 && (
                        <div style={{ marginTop: "1rem" }}>
                            <p
                                style={{
                                    margin: "0 0 0.25rem",
                                    fontSize: "0.8rem",
                                    fontWeight: 700,
                                    color: "#dc2626",
                                }}
                            >
                                To render an image of the doorLeft, please fill out the following
                                size:
                            </p>
                            {renderValidations.map((v, i) => (
                                <p
                                    key={i}
                                    style={{
                                        margin: "0.1rem 0",
                                        fontSize: "0.78rem",
                                        color: "#dc2626",
                                    }}
                                >
                                    * {v}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FormSaleDoor;
